// [S-534] Google Picker integration — lazily loads Google's client + picker
// SDK and opens the native file picker, resolving the selected files' ids.
//
// This is the one piece that can't be unit-tested (it drives a Google-hosted
// dialog), so it's isolated behind `openDrivePicker`; everything around it (the
// button states, the honest degrade, the import call) lives in testable code
// that mocks this module. A narrow typed facade over the dynamic SDK keeps it
// lint-clean without `any`.

export type PickedFile = { id: string; name: string; mimeType: string };

export type OpenPickerOptions = {
  /** Browser API key for the Picker (VITE_GOOGLE_PICKER_API_KEY). */
  apiKey: string;
  /** Short-lived OAuth access token for the caller's Drive grant. */
  oauthToken: string;
  /** GCP project number — required by the Picker for some scopes. Optional. */
  appId?: string;
};

// ── Minimal typed facade over the dynamic google.picker SDK ──────────────────
interface PickerDoc {
  id: string;
  name: string;
  mimeType: string;
}
interface PickerResponse {
  action: string;
  docs?: PickerDoc[];
}
interface PickerInstance {
  setVisible(visible: boolean): void;
}
interface PickerBuilder {
  enableFeature(feature: unknown): PickerBuilder;
  setDeveloperKey(key: string): PickerBuilder;
  setAppId(appId: string): PickerBuilder;
  setOAuthToken(token: string): PickerBuilder;
  addView(view: unknown): PickerBuilder;
  setCallback(cb: (data: PickerResponse) => void): PickerBuilder;
  build(): PickerInstance;
}
interface DocsView {
  setIncludeFolders(include: boolean): DocsView;
  setSelectFolderEnabled(enabled: boolean): DocsView;
}
interface GooglePicker {
  PickerBuilder: new () => PickerBuilder;
  DocsView: new (viewId: unknown) => DocsView;
  ViewId: { DOCS: unknown };
  Feature: { MULTISELECT_ENABLED: unknown };
  Action: { PICKED: string; CANCEL: string };
}
interface GapiWindow {
  gapi?: { load(api: string, opts: { callback: () => void }): void };
  google?: { picker?: GooglePicker };
}

const GAPI_SRC = "https://apis.google.com/js/api.js";
let pickerReady: Promise<GooglePicker> | null = null;

function loadPickerSdk(): Promise<GooglePicker> {
  if (pickerReady !== null) return pickerReady;
  pickerReady = new Promise<GooglePicker>((resolve, reject) => {
    const w = window as unknown as GapiWindow;
    const done = (): void => {
      const gapi = w.gapi;
      if (gapi === undefined) {
        reject(new Error("Google API failed to load"));
        return;
      }
      gapi.load("picker", {
        callback: () => {
          const picker = w.google?.picker;
          if (picker === undefined) reject(new Error("Google Picker failed to load"));
          else resolve(picker);
        },
      });
    };
    if (w.gapi !== undefined) {
      done();
      return;
    }
    const script = document.createElement("script");
    script.src = GAPI_SRC;
    script.async = true;
    script.onload = done;
    script.onerror = () => {
      pickerReady = null; // allow a retry on next open
      reject(new Error("Could not reach Google — check your connection."));
    };
    document.head.appendChild(script);
  });
  return pickerReady;
}

/**
 * Open the Google Picker and resolve with the files the user selected (empty
 * array if they cancel). Rejects only if the SDK can't load.
 */
export async function openDrivePicker(
  opts: OpenPickerOptions
): Promise<PickedFile[]> {
  const picker = await loadPickerSdk();
  return new Promise<PickedFile[]>((resolve) => {
    const view = new picker.DocsView(picker.ViewId.DOCS)
      .setIncludeFolders(true)
      .setSelectFolderEnabled(false);
    let builder = new picker.PickerBuilder()
      .enableFeature(picker.Feature.MULTISELECT_ENABLED)
      .setDeveloperKey(opts.apiKey)
      .setOAuthToken(opts.oauthToken)
      .addView(view);
    if (opts.appId !== undefined && opts.appId.length > 0) {
      builder = builder.setAppId(opts.appId);
    }
    const instance = builder
      .setCallback((data: PickerResponse) => {
        if (data.action === picker.Action.PICKED) {
          resolve(
            (data.docs ?? []).map((d) => ({
              id: d.id,
              name: d.name,
              mimeType: d.mimeType,
            }))
          );
        } else if (data.action === picker.Action.CANCEL) {
          resolve([]);
        }
      })
      .build();
    instance.setVisible(true);
  });
}

/** Frontend config for the Picker — present only when the API key is set. */
export function drivePickerConfig(): { apiKey: string; appId?: string } | null {
  const apiKey = import.meta.env.VITE_GOOGLE_PICKER_API_KEY ?? "";
  if (apiKey.length === 0) return null;
  const appId = import.meta.env.VITE_GOOGLE_PICKER_APP_ID ?? "";
  return appId.length > 0 ? { apiKey, appId } : { apiKey };
}
